import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { IPbaUser, ProductUserRole } from '../../../../types/d3ePolicy';
import { PbaPasswordRequirementsComponent } from '../pba-password-requirements/pba-password-requirements.component';
import { WindowService } from '../../../../services/window.service';

const USER_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[~! @#$^&*()_\-+=[\]:<>.]).*$/;

const PASSWORD_CHARSET =
  'abcdefghijklmnopqrstuvwxyz' +
  'ABCDEFGHIJKLMNOPQRSTUVWXYZ' +
  '0123456789' +
  '~! @#$^&*()_-+=[]:<>.';

export type EndpointUserType = 'pba' | 'fde';
export type EndpointUserDialogMode = 'add' | 'edit';

export type IEndpointUser = IPbaUser;

export interface IUserEndpoint {
  licenseId: number;
  endpointName: string;
  diskSerial: string;
  pwMin: number | undefined;
}

export interface IEndpointUserDialogOptions {
  userType: EndpointUserType;
  mode: EndpointUserDialogMode;
  //existingUsers: IEndpointUser[];
  endpoints: IUserEndpoint[];
  user?: IEndpointUser;
}

export interface IEndpointUserResult {
  userType: EndpointUserType;
  mode: EndpointUserDialogMode;
  users: IEndpointUserResultRow[];
}

export interface IEndpointUserResultRow {
  licenseId: number;
  endpointName: string;
  disk_serial: string;
  username: string;
  email: string;
  role: ProductUserRole;
  password: string;
}

@Component({
  selector: 'app-user-dialog',
  templateUrl: './endpoint-user-dialog.component.html',
  styleUrl: './endpoint-user-dialog.component.scss',
  standalone: false
})
export class EndpointUserDialogComponent {
  form: FormGroup;

  readonly roles = [
    {
      value: ProductUserRole.User,
      label: 'User',
    },
    {
      value: ProductUserRole.Admin,
      label: 'Admin',
    },
    {
      value: ProductUserRole.UserAdmin,
      label: 'UserAdmin',
    },
  ];

  private readonly passwordMin: number;
  private readonly passwordMinimumDefault = 8;
  private readonly passwordMinimumMax = 128;

  constructor(
    public readonly dialogRef: MatDialogRef<EndpointUserDialogComponent>,
    private readonly dialog: MatDialog,
    private readonly windowService: WindowService,
    @Inject(MAT_DIALOG_DATA) public options: IEndpointUserDialogOptions
  ) {
    this.passwordMin = Math.max(
      ...this.options.endpoints.map(endpoint => endpoint.pwMin ?? this.passwordMinimumDefault),
      this.passwordMinimumDefault
    );

    const usernameValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
      const username = control.value;

      if (!username)
        return null;

      if (this.options.mode === 'add') {
        //const exists = this.options.existingUsers?.some(user => user.username === username);
        return null;//exists ? { usernameExists: true } : null;
      }

      if (this.options.mode === 'edit') {
        return username !== this.options.user?.username ? { usernameMissing: true } : null;
      }

      return null;
    };

    const passwordEmptyOrCompliant: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      return this.isValidPassword(control.value) ? null : { passwordValid: true };
    };

    const passwordValidators: ValidatorFn[] =
      this.options.mode === 'add'
        ? [
          Validators.required,
          Validators.minLength(this.passwordMin),
          Validators.maxLength(this.passwordMinimumMax),
          Validators.pattern(USER_PASSWORD_REGEX),
        ]
        : [
          passwordEmptyOrCompliant,
          Validators.minLength(this.passwordMin),
          Validators.maxLength(this.passwordMinimumMax),
        ];

    this.form = new FormGroup({
      username: new FormControl(this.options.user?.username ?? '', {
        validators: [
          Validators.required,
          usernameValidator,
        ],
      }),
      email: new FormControl(this.options.user?.email ?? ''),
      password: new FormControl('', {
        validators: passwordValidators,
      }),
      role: new FormControl(
        this.options.user?.role ?? ProductUserRole.User,
        Validators.required
      ),
    }, {
      updateOn: 'change',
    });

    if (this.options.mode === 'add') {
      this.generatePassword();
    }

    if (this.options.mode === 'edit') {
      this.form.controls.username.disable();
    }
  }

  get dialogTitle(): string {
      const action = this.options.mode === 'add' ? 'Add' : 'Edit';
      const product = this.options.userType.toUpperCase();
      const user = this.options.endpoints.length === 1 ? 'User' : 'Users';

      return `${action} ${product} ${user}`;
  }

  get endpointCount(): number {
    return this.options.endpoints.length;
  }

  generatePassword(): void {
    const password = this.randomPassword(this.passwordMin);

    this.form.controls.password.setValue(password);
  }

  private randomPassword(length: number): string {
    let password: string;

    do {
      password = '';

      for (let i = 0; i < length; ++i) {
        password += PASSWORD_CHARSET.charAt(
          Math.floor(
            this.windowService.cryptoRandom() *
            PASSWORD_CHARSET.length
          )
        );
      }
    } while (!this.isValidPassword(password));

    return password;
  }

  private isValidPassword(password: string): boolean {
    return (password.length >= this.passwordMin && USER_PASSWORD_REGEX.test(password));
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const username = this.form.controls.username.getRawValue();

    const result: IEndpointUserResult = {
      userType: this.options.userType,
      mode: this.options.mode,
      users: this.options.endpoints.map(endpoint => ({
        licenseId: endpoint.licenseId,
        endpointName: endpoint.endpointName,
        disk_serial: endpoint.diskSerial,
        username,
        email: this.form.controls.email.value ?? '',
        role: this.form.controls.role.value,
        password: this.form.controls.password.value ?? '',
      })),
    };

    this.dialogRef.close(result);
  }

  passwordRequirements(): void {
    this.dialog.open(PbaPasswordRequirementsComponent);
  }

  get endpointNames(): string {
      return this.options.endpoints
          .map(endpoint => endpoint.endpointName)
          .join(', ');
  }
}